"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MetricsListV2 = exports.MetricsList = void 0;
const lodash_1 = require("lodash");
const global_constants_1 = require("@contentsquare/global-constants");
const global_1 = require("../global");
const metrics_1 = require("../metrics");
/**
 * @deprecated Use MetricsListV2 instead
 */
class MetricsList {
    static compose(...fns) {
        return (0, lodash_1.reduceRight)(fns, (accumulatedFn, currentFn) => (val) => accumulatedFn(currentFn(val)), (vals) => vals);
    }
    static filterMetrics(...filterFns) {
        if (filterFns.length) {
            const composedFiltersFns = filterFns.map((filterFn) => (metrics) => metrics.filter((metric) => filterFn(Object.assign({}, metric))));
            return MetricsList.compose(...composedFiltersFns);
        }
        return MetricsList.identical;
    }
    static identical(vals) {
        return vals;
    }
    static filterByProjectType(isSDK) {
        const projectTypeFilter = (metric) => (0, lodash_1.isBoolean)(isSDK)
            ? isSDK
                ? (0, lodash_1.intersection)([global_constants_1.Device.MobileSdk, global_constants_1.Device.TabletSdk], metric.devices).length > 0
                : (0, lodash_1.intersection)([global_constants_1.Device.All, global_constants_1.Device.Desktop, global_constants_1.Device.Mobile, global_constants_1.Device.Tablet], metric.devices).length > 0
            : true;
        return MetricsList.filterMetrics(projectTypeFilter);
    }
    static filterById(id) {
        const idFilter = (metric) => !id || metric.id === id;
        return MetricsList.filterMetrics(idFilter);
    }
    static filterByDevice(devices) {
        const devicesFilter = (metric) => !devices || (0, lodash_1.intersection)(devices, metric.devices).length > 0;
        return MetricsList.filterMetrics(devicesFilter);
    }
    static filterByInterface(metricInterface) {
        const interfaceFilter = (metric) => {
            if (Array.isArray(metricInterface)) {
                return metricInterface.some((metricInterface) => {
                    const interfaceAsArray = Array.isArray(metric.interface) ? metric.interface : [metric.interface];
                    return interfaceAsArray.some((m) => m === metricInterface);
                });
            }
            else {
                if (metricInterface) {
                    const interfaceAsArray = Array.isArray(metric.interface) ? metric.interface : [metric.interface];
                    return interfaceAsArray.some((m) => m === metricInterface);
                }
                return true;
            }
        };
        return MetricsList.filterMetrics(interfaceFilter);
    }
    static getAllBy(filters, metrics) {
        const { isSDKProject, metricId, devices, metricInterface } = filters;
        return MetricsList.compose(MetricsList.filterByProjectType(isSDKProject), MetricsList.filterById(metricId), MetricsList.filterByInterface(metricInterface), MetricsList.filterByDevice(devices))(metrics);
    }
    // exposed APIs
    static getAll(filters = {}) {
        const metrics = [...MetricsList.allMetrics];
        if (filters.withCoreWebVitals) {
            metrics.push(...(0, global_1.getCwvMetrics)(filters.operator));
            const metric = metrics.find((metric) => metric.id === 'loadingTime');
            if (metric) {
                metric.name = 'METRIC.LOADING_TIME_DEPRECATED';
            }
        }
        return MetricsList.getAllBy(filters, metrics);
    }
    static getCategorizedMetrics(filters) {
        const metricsWithCategory = MetricsList.getAll(filters).filter((metric) => !!metric.category);
        const groupedMetrics = (0, lodash_1.groupBy)(metricsWithCategory, 'category');
        return Object.entries(groupedMetrics).reduce((acc, [categoryId, metrics]) => {
            return [
                ...acc,
                {
                    categoryName: global_1.MetricCategoryKey[categoryId],
                    items: metrics,
                },
            ];
        }, []);
    }
    static getById(metricId, metrics = MetricsList.allMetrics) {
        // N.B. possible duplicate id (EX: BounceRate exists for both site & page)
        return MetricsList.getAllBy({ metricId }, metrics)[0];
    }
    static getZoneMetrics(filters) {
        return MetricsList.getAllBy(filters, MetricsList.zoneMetrics);
    }
    static getPageMetrics(filters) {
        return MetricsList.getAllBy(filters, MetricsList.pageMetrics);
    }
    static getSiteMetrics(filters) {
        return MetricsList.getAllBy(filters, MetricsList.siteMetrics);
    }
    static getNRTMetrics(filters) {
        return MetricsList.getAllBy(filters, MetricsList.nrtMetrics);
    }
    static getTZMMetrics(filters) {
        return MetricsList.getAllBy(filters, MetricsList.tzmMetrics);
    }
    static getCoreWebVitalsMetrics(filters) {
        return MetricsList.getAllBy(filters, (0, global_1.getCwvMetrics)(filters.operator));
    }
    static getZoneMetricById(metricId) {
        return MetricsList.getAllBy({ metricId }, MetricsList.zoneMetrics)[0];
    }
    static getPageMetricById(metricId) {
        return MetricsList.getAllBy({ metricId }, MetricsList.pageMetrics)[0];
    }
    static getSiteMetricById(metricId) {
        return MetricsList.getAllBy({ metricId }, MetricsList.siteMetrics)[0];
    }
    static getNRTMetricById(metricId) {
        return MetricsList.getAllBy({ metricId }, MetricsList.nrtMetrics)[0];
    }
    static getTZMMetricById(metricId) {
        return MetricsList.getAllBy({ metricId }, MetricsList.tzmMetrics)[0];
    }
    static getCoreWebVitalsMetricById(metricId, operator) {
        return MetricsList.getAllBy({ metricId }, (0, global_1.getCwvMetrics)(operator))[0];
    }
    static getByDevices(filters, metrics = MetricsList.allMetrics) {
        return MetricsList.getAllBy(filters, metrics);
    }
}
exports.MetricsList = MetricsList;
MetricsList.siteMetrics = metrics_1.SITE_METRICS;
MetricsList.zoneMetrics = metrics_1.ZONE_METRICS;
MetricsList.pageMetrics = metrics_1.PAGE_METRICS;
MetricsList.nrtMetrics = metrics_1.NRT_METRICS;
MetricsList.tzmMetrics = metrics_1.TZM_METRICS;
MetricsList.allMetrics = metrics_1.ALL_METRICS;
class MetricsListV2 {
    constructor(allMetrics = metrics_1.ALL_METRICS) {
        this.siteMetrics = metrics_1.SITE_METRICS;
        this.pageMetrics = metrics_1.PAGE_METRICS;
        this.nrtMetrics = metrics_1.NRT_METRICS;
        this.tzmMetrics = metrics_1.TZM_METRICS;
        this.zoneMetrics = metrics_1.ZONE_METRICS;
        this.allMetrics = allMetrics;
    }
    compose(...fns) {
        return (0, lodash_1.reduceRight)(fns, (accumulatedFn, currentFn) => (val) => accumulatedFn(currentFn(val)), (vals) => vals);
    }
    filterMetrics(...filterFns) {
        if (filterFns.length) {
            const composedFiltersFns = filterFns.map((filterFn) => (metrics) => metrics.filter((metric) => filterFn(Object.assign({}, metric))));
            return this.compose(...composedFiltersFns);
        }
        return this.identical;
    }
    identical(vals) {
        return vals;
    }
    filterByProjectType(isSDK) {
        const projectTypeFilter = (metric) => (0, lodash_1.isBoolean)(isSDK)
            ? isSDK
                ? (0, lodash_1.intersection)([global_constants_1.Device.MobileSdk, global_constants_1.Device.TabletSdk], metric.devices).length > 0
                : (0, lodash_1.intersection)([global_constants_1.Device.All, global_constants_1.Device.Desktop, global_constants_1.Device.Mobile, global_constants_1.Device.Tablet], metric.devices).length > 0
            : true;
        return this.filterMetrics(projectTypeFilter);
    }
    filterById(id) {
        const idFilter = (metric) => !id || metric.id === id;
        return this.filterMetrics(idFilter);
    }
    filterByDevice(devices) {
        const devicesFilter = (metric) => !devices || (0, lodash_1.intersection)(devices, metric.devices).length > 0;
        return this.filterMetrics(devicesFilter);
    }
    filterByInterface(metricInterface) {
        const interfaceFilter = (metric) => {
            if (Array.isArray(metricInterface)) {
                return metricInterface.some((metricInterface) => {
                    const interfaceAsArray = Array.isArray(metric.interface) ? metric.interface : [metric.interface];
                    return interfaceAsArray.some((m) => m === metricInterface);
                });
            }
            else {
                if (metricInterface) {
                    const interfaceAsArray = Array.isArray(metric.interface) ? metric.interface : [metric.interface];
                    return interfaceAsArray.some((m) => m === metricInterface);
                }
                return true;
            }
        };
        return this.filterMetrics(interfaceFilter);
    }
    getAllBy(filters, metrics) {
        const { isSDKProject, metricId, devices, metricInterface } = filters;
        return this.compose(this.filterByProjectType(isSDKProject), this.filterById(metricId), this.filterByInterface(metricInterface), this.filterByDevice(devices))(metrics);
    }
    // exposed APIs
    getAll(filters = {}) {
        const metrics = [...this.allMetrics];
        if (filters.withCoreWebVitals) {
            metrics.push(...(0, global_1.getCwvMetrics)(filters.operator));
            const metric = metrics.find((metric) => metric.id === 'loadingTime');
            if (metric) {
                metric.name = 'METRIC.LOADING_TIME_DEPRECATED';
            }
        }
        return this.getAllBy(filters, metrics);
    }
    getCategorizedMetrics(filters) {
        const metricsWithCategory = this.getAll(filters).filter((metric) => !!metric.category);
        const groupedMetrics = (0, lodash_1.groupBy)(metricsWithCategory, 'category');
        return Object.entries(groupedMetrics).reduce((acc, [categoryId, metrics]) => {
            return [
                ...acc,
                {
                    categoryName: global_1.MetricCategoryKey[categoryId],
                    items: metrics,
                },
            ];
        }, []);
    }
    getById(metricId, metrics = this.allMetrics) {
        // N.B. possible duplicate id (EX: BounceRate exists for both site & page)
        return this.getAllBy({ metricId }, metrics)[0];
    }
    getZoneMetrics(filters) {
        return this.getAllBy(filters, this.zoneMetrics);
    }
    getPageMetrics(filters) {
        return this.getAllBy(filters, this.pageMetrics);
    }
    getSiteMetrics(filters) {
        return this.getAllBy(filters, this.siteMetrics);
    }
    getNRTMetrics(filters) {
        return this.getAllBy(filters, this.nrtMetrics);
    }
    getTZMMetrics(filters) {
        return this.getAllBy(filters, this.tzmMetrics);
    }
    getCoreWebVitalsMetrics(filters) {
        return this.getAllBy(filters, (0, global_1.getCwvMetrics)(filters.operator));
    }
    getZoneMetricById(metricId) {
        return this.getAllBy({ metricId }, this.zoneMetrics)[0];
    }
    getPageMetricById(metricId) {
        return this.getAllBy({ metricId }, this.pageMetrics)[0];
    }
    getSiteMetricById(metricId) {
        return this.getAllBy({ metricId }, this.siteMetrics)[0];
    }
    getNRTMetricById(metricId) {
        return this.getAllBy({ metricId }, this.nrtMetrics)[0];
    }
    getTZMMetricById(metricId) {
        return this.getAllBy({ metricId }, this.tzmMetrics)[0];
    }
    getCoreWebVitalsMetricById(metricId, operator) {
        return this.getAllBy({ metricId }, (0, global_1.getCwvMetrics)(operator))[0];
    }
    getByDevices(filters, metrics) {
        return this.getAllBy(filters, metrics || this.allMetrics);
    }
    setMetrics(metricIds, options) {
        this.allMetrics = metricIds.length
            ? metricIds.map((metric) => this.getMetricById(metric, options.withNearRealTime))
            : metrics_1.ALL_METRICS_V2;
        const metricUniqIds = new Set(...this.allMetrics.map((metric) => metric.id));
        this.siteMetrics = metrics_1.SITE_METRICS.filter((metric) => metricUniqIds.has(metric.id));
        this.zoneMetrics = metrics_1.ZONE_METRICS.filter((metric) => metricUniqIds.has(metric.id));
        this.pageMetrics = metrics_1.PAGE_METRICS.filter((metric) => metricUniqIds.has(metric.id));
        this.nrtMetrics = metrics_1.NRT_METRICS.filter((metric) => metricUniqIds.has(metric.id));
        this.tzmMetrics = metrics_1.TZM_METRICS.filter((metric) => metricUniqIds.has(metric.id));
    }
    getMetricById(id, withNearRealTime, metricInterface) {
        // Keep NRT check first because we don't want other metrics in this case
        if (withNearRealTime) {
            return this.getNRTMetricById(id);
        }
        if ((0, global_1.isCwvMetric)(id)) {
            const { operator } = (0, global_1.splitPartialIdAndOperator)(id);
            return this.getCoreWebVitalsMetricById(id, operator);
        }
        switch (metricInterface) {
            case global_constants_1.Interface.Zone:
                return this.getZoneMetricById(id);
            case global_constants_1.Interface.Page:
                return this.getPageMetricById(id);
            case global_constants_1.Interface.Site:
                return this.getSiteMetricById(id);
            default:
                return this.getById(id);
        }
    }
}
exports.MetricsListV2 = MetricsListV2;
//# sourceMappingURL=metrics-service.js.map